import { GeneralSuccessResponse } from '@data-types/generic';
import { UserType } from '@enums/index';
import { authSuccess } from '@modules/auth/lib/authSlice';
import { Mentee, Mentor } from '@modules/auth/types';
import { showToast } from '@slices/apiSlice';
import { baseApi } from '@store/baseQuery';
import { RootState } from '@store/store';

import { DeleteImagePayload } from '../types';

export const personalprofileInfo = baseApi.injectEndpoints({
    endpoints: builder => ({
        getMentorPersonalProfile: builder.query<Mentor, { username: string }>({
            query: args => ({
                url: `/mentor/${args.username}`,
                method: 'GET',
            }),
            providesTags: ['Personal'],
            async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data } = await queryFulfilled;
                    const { user } = (getState() as RootState).reducer.auth;
                    if (user?.userType === UserType.Mentor && user?.username === data?.username) {
                        dispatch(
                            authSuccess({
                                user: data,
                            })
                        );
                    }
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        getMenteePersonalProfile: builder.query<Mentee, { username: string }>({
            query: args => ({
                url: `/mentee/${args.username}`,
                method: 'GET',
            }),
            providesTags: ['Personal'],
            async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        authSuccess({
                            user: data,
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        updateMentorPersonalProfile: builder.mutation<Mentor, FormData>({
            query: body => ({
                url: '/mentor/me',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Personal'],
            async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        authSuccess({
                            user: data,
                        })
                    );
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Personal Info Updated successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        updateMenteePersonalProfile: builder.mutation<Mentee, FormData>({
            query: body => ({
                url: '/mentee/me',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Personal'],
            async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        authSuccess({
                            user: data,
                        })
                    );
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Personal Info Updated successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),

        submitMentorProfile: builder.mutation<{ message: string }, void>({
            query: body => ({
                url: '/mentor/submit-profile',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Personal'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Profile submitted successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        deleteMentorProfilePic: builder.mutation<GeneralSuccessResponse, DeleteImagePayload>({
            query: body => ({
                url: `/mentor/profile-image`,
                method: 'DELETE',
                body,
            }),
            invalidatesTags: ['Personal'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Profile Picture deleted successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        deleteMenteeProfilePic: builder.mutation<GeneralSuccessResponse, DeleteImagePayload>({
            query: body => ({
                url: `/mentee/profile-image`,
                method: 'DELETE',
                body,
            }),
            invalidatesTags: ['Personal'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Profile Picture deleted successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
    }),
    overrideExisting: true,
});

export const {
    useLazyGetMentorPersonalProfileQuery,
    useLazyGetMenteePersonalProfileQuery,
    useUpdateMentorPersonalProfileMutation,
    useUpdateMenteePersonalProfileMutation,
    useSubmitMentorProfileMutation,
    useDeleteMentorProfilePicMutation,
    useDeleteMenteeProfilePicMutation,
} = personalprofileInfo;
